import { IconNames, Text } from '@dixa/lib-shared-habla';
import { v4 as uuid } from 'uuid';

import {
  FlowManagerNodeComponentT,
  FlowNode,
  flowNodeColor,
  FlowType,
  NodeTypeComponent,
} from 'fable/flowEditor';
import type {
  InternalPortT,
  NodeT,
  RegisterPortT,
  ScopeVarsT,
} from 'fable/flowEditor';

import ActionModal from '../../containers/modals/filters';
import {
  FlowCategorySelectItem,
  SystemVariableType,
} from '../../helpers/flowVariableHelpers';
import { formatPortsHelper } from '../../helpers/nodePortFormatter';
import { MatchIdType } from '../modals/filters/criteriaFields';
import {
  ConditionOutcome,
  FlowNodeTypes,
  MatchingOperator,
  MatchingStrategy,
} from '../types';

export interface DateObject {
  date?: string;
  localDate?: string;
  localTimezone?: string;
  timezone?: string;
}

export type DateRangeObject = {
  startDate?: string;
  localStartDate?: string;
  endDate?: string;
  localEndDate?: string;
  timezone?: string;
  localTimezone?: string;
};

export interface FiltersNodeRuleMatch {
  id: string;
  type: string;
  value?: string | DateRange | number | string[];
}

export interface FiltersNodeRuleVariable {
  id: string;
  type: string;
  value?: string | DateRange | number | string[];
}

export type DateRange = {
  start_date: string;
  end_date: string;
};

export interface FiltersNodeRule {
  match: FiltersNodeRuleMatch;
  matching_rule: MatchingOperator;
  variable: FiltersNodeRuleVariable;
}

export interface FiltersFormRule {
  match: string | DateObject | DateRangeObject | number | string[];
  matching_rule: MatchingOperator;
  variable?: string | FlowCategorySelectItem['id'];
  variableType?: MatchIdType;
  custom_attribute_id?: string;
  match_custom_attribute_id?: string;
}

interface FlowNodeFiltersPayload {
  match_node_id?: string | null;
  matching_strategy: MatchingStrategy;
  non_match_node_id?: string | null;
  rules: FiltersNodeRule[];
  description?: string;
}

export type FlowNodeFilters = NodeT<
  FlowNodeTypes.FILTERS,
  FlowNodeFiltersPayload
>;

type PropsT = FlowManagerNodeComponentT<FlowNodeFilters, any>;

const portTranslator = (node: FlowNodeFilters): RegisterPortT[] => [
  {
    id: uuid(),
    type: 'out',
    label: ConditionOutcome.NO_MATCH,
    connection: node.payload.non_match_node_id || uuid(),
  },
  {
    id: uuid(),
    type: 'out',
    label: ConditionOutcome.MATCH,
    connection: node.payload.match_node_id || uuid(),
  },
];

const createFiltersNode = (flowType: FlowType = FlowType.FLOW_TYPE_CALL) => {
  return class FiltersNode extends NodeTypeComponent<FlowNodeFilters>() {
    static icon: IconNames = 'branch';
    static label = 'Filters';
    static helpText =
      'Sort conversations based on whether they match your chosen criteria.';
    static color = flowNodeColor.purple;
    static height = FlowNode.dimensions.h + 16;
    static translatePayload = formatPortsHelper(portTranslator);
    static ActionModal = ActionModal(flowType);
    static actionTrigger = { type: FlowNodeTypes.FILTERS };
    static translateScopeVariables = (node: FlowNodeFilters): ScopeVarsT => {
      const { payload: { rules } = {} } = node;
      const needs: string[] = [];

      if (Array.isArray(rules)) {
        rules.forEach((rule: FiltersNodeRule) => {
          if (
            rule.variable?.type !== SystemVariableType.System &&
            rule.variable.id
          ) {
            needs.push(rule.variable.id);
          }

          const isMatchScopeVariable =
            rule.match?.type === SystemVariableType.Scope;
          if (rule.match.id && isMatchScopeVariable) {
            needs.push(rule.match.id);
          }
        });
      }

      return {
        needs: [...new Set(needs)],
        grants: [],
      };
    };
    static updateConnection = (
      node: FlowNodeFilters,
      port: InternalPortT,
      newConnection: string,
    ): FlowNodeFilters => {
      const payload = { ...node.payload };
      payload[
        port.label === ConditionOutcome.MATCH
          ? 'match_node_id'
          : 'non_match_node_id'
      ] = newConnection;
      return { ...node, payload };
    };

    constructor(props: PropsT) {
      super(props);

      /** We have to bind this way. @boundMethod doesn't work */
      this.triggerEdit = this.triggerEdit.bind(this);
    }

    triggerEdit() {
      const { onEdit, node } = this.props;
      onEdit(node, node.nodeType);
    }

    render() {
      const { node } = this.props;
      const { rules, description = '' } = node.payload;
      const hasDescription = !!description;

      return (
        <FlowNode
          color={flowNodeColor.purple}
          icon={FiltersNode.icon}
          label={FiltersNode.label}
          text={
            <div style={{ height: '32px' }}>
              <Text.Paragraph clamp={1} variant="3" as="strong">
                {hasDescription ? description : 'Add a description'}
              </Text.Paragraph>
              <Text.Paragraph variant="3" as="strong">
                {rules.length} {rules.length === 1 ? 'rule' : 'rules'}
              </Text.Paragraph>
            </div>
          }
          onEdit={this.triggerEdit}
        />
      );
    }
  };
};

export const FiltersNodeEmail = createFiltersNode(FlowType.FLOW_TYPE_EMAIL);
export const FiltersNodePhone = createFiltersNode(FlowType.FLOW_TYPE_CALL);
export const FiltersNodeMessenger = createFiltersNode(
  FlowType.FLOW_TYPE_MESSENGER,
);
